import { Card, Spin } from "antd";
import React, {useEffect, useState} from "react";
import { CalendarOutlined } from "@ant-design/icons";
import {FormattedMessage} from "react-intl";
import { Link } from "react-router-dom";
import { getDoctorColor, mapCalendarEventsToWashEvents } from "../../utils/calendarUtils";
import {getCalendarApi, getMedicalStaffApi, getPatientApi} from "../../api/apiInstances";
import {MedicalStaff, Patient} from "../../api-client";
import dayjs from "dayjs";
import { WashEvent } from "../../types";
import GetDisplayName from  "../calendar/GetDisplayName";
import logger from "../../utils/logger";
import { MAX_PATIENTS_PER_PAGE } from "../common/Constants";

const DashboardCalendarCard = () => {
    const [appointments, setAppointments] = useState<WashEvent[]>([]);
    const [patients, setPatients] = useState<Map<string, Patient>>(new Map());
    const [medicalStaff, setMedicalStaff] = useState<Map<string, MedicalStaff>>(new Map());
    const [loading, setLoading] = useState<boolean>(true);

    const calendarApi = getCalendarApi();
    const patientApi = getPatientApi();
    const medicalApi = getMedicalStaffApi();

    useEffect(() => {
        fetchCalendarEvents();
    }, []);

    const fetchCalendarEvents = async () => {
        try {
            const res = await calendarApi.fetchLastThreeCalendarEvents();
            const events = mapCalendarEventsToWashEvents(res.data);
            setAppointments(events);
            await fetchPatients();
        } catch (err) {
            logger.log(err);
            setLoading(false);
        }
    };

    const fetchPatients = async () => {
        try {
            const res = await patientApi.fetchAllPatients('', 'id', 'asc', 0, MAX_PATIENTS_PER_PAGE);
            const patientMap = new Map();
            for (const patient of res.data.patients!) {
                patientMap.set(patient.id, patient);
            }
            setPatients(patientMap);
            await fetchMedicalStaff();
        } catch (err) {
            logger.log(err);
        }
    };

    const fetchMedicalStaff = async () => {
        try {
            const res = await medicalApi.fetchAllMedicalStaff('', 'id', 1, 10000);
            const medicalStaffMap = new Map();
            for (const doctor of res.data.medicalStaff!) {
                medicalStaffMap.set(doctor.id, doctor);
            }
            setMedicalStaff(medicalStaffMap);
        } catch (err) {
            logger.log(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card
            bordered={true}
            style={{ height: 336, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
            styles={{body: { padding: '0', flex: 1, display: 'flex', flexDirection: 'column' }}}
            className="dentobook-dashboard-card dentobook-dashboard-calendar-card"
        >
            <span className="font-24 dentobook-card-heading">
                <span className="text-blue mr-1"><CalendarOutlined /></span> {<FormattedMessage id="nextHours" />}
            </span>
            <div className="mt-2 mb-24" style={{ flex: 1 }}>
                <div className="divider-line"></div>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin size="small" />
                    </div>
                ) : (
                    appointments.map(appointment => {
                    const patient = patients?.get(appointment.patient);
                    const doctor = medicalStaff?.get(appointment.doctor);

                    return (
                        <React.Fragment key={appointment.id}>
                            <div
                                className="calendar-tile"
                                style={{ backgroundColor: getDoctorColor(appointment.doctor) }}
                            >
                                <div className="font-family-roboto font-weight-700 text-black font-10">
                                    <GetDisplayName
                                        firstName={patient?.firstName}
                                        lastName={patient?.lastName}
                                    />
                                </div>
                                <div className="font-family-roboto font-weight-400 text-black font-10">
                                    <FormattedMessage id="dr" />{" "}
                                    <GetDisplayName
                                        firstName={doctor?.firstName}
                                        lastName={doctor?.lastName}
                                    />
                                </div>
                                <div className="font-family-roboto font-weight-400 text-black font-10">
                                    {dayjs(appointment.start).format("HH:mm")} -{" "}
                                    {dayjs(appointment.end).format("HH:mm")}
                                </div>
                            </div>
                            <div className="divider-line"></div>
                        </React.Fragment>
                    );
                })
                )}
            </div>
            <Link to="/calendar" className="dentobook-btn-secondary dentobook-btn-medium"
                  style={{ margin: "auto", width: '170px', marginBottom: '0px', marginTop: '-15px' }}>
                <FormattedMessage id='toCalendar' />
            </Link>
        </Card>
    );
};

export default DashboardCalendarCard;

